// Tax-treaty relief claims: dated, evidence-backed, approved under segregation of
// duties, then applied to a line whose rule permits a treaty override. Relief is
// never inferred from a flag — it always comes from an approved, valid claim.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/features/shared/components/ui/select';
import { commissionApi, type TreatyClaim, type CommissionPayee } from '../api/commissionApi';
import { getApiErrorMessage } from '@/lib/apiError';

const STATUS: Record<string, 'secondary' | 'warning' | 'success' | 'danger'> = {
  pending: 'warning', approved: 'success', rejected: 'danger', expired: 'secondary',
};
const CLASSIFICATIONS = ['PROFESSIONAL_SERVICE_FEE', 'AGENT_COMMISSION', 'MANAGEMENT_FEE', 'TECHNICAL_SERVICE_FEE', 'DIRECTOR_FEE', 'REFERRAL_FEE', 'OTHER'];

export function TreatyClaimsPanel({ payees, canApprove }: { payees: CommissionPayee[]; canApprove: boolean }) {
  const [claims, setClaims] = useState<TreatyClaim[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [c, setC] = useState({ payee_id: '', treaty_country: '', payment_classification: 'PROFESSIONAL_SERVICE_FEE', treaty_article: '', approved_rate: '0.08', valid_from: '', valid_to: '', certificate_ref: '', reason: '' });

  const refresh = async () => { try { setClaims(await commissionApi.listTreatyClaims()); } catch { setClaims([]); } };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!c.payee_id || !c.certificate_ref || !c.reason || !c.valid_from || !c.valid_to) { toast.error('Payee, validity dates, certificate and reason are required'); return; }
    setBusy('new');
    try { await commissionApi.createTreatyClaim(c); toast.success('Treaty claim submitted for approval'); setOpen(false); await refresh(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, 'Could not create claim')); }
    finally { setBusy(null); }
  };
  const action = async (id: string, act: 'approve' | 'reject') => {
    setBusy(id);
    try { await commissionApi.treatyClaimAction(id, act); toast.success(`Claim ${act}d`); await refresh(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, 'Action failed')); }
    finally { setBusy(null); }
  };

  if (claims === null) return null;
  const payeeName = (id: string) => payees.find((p) => p.payee_id === id)?.legal_name || id.slice(0, 8);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground"><ShieldCheck className="h-4 w-4" /> Treaty relief claims</div>
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)}>New claim</Button>
        </div>
        {open && (
          <div className="mb-3 grid grid-cols-2 gap-2 rounded-md border border-border p-3">
            <div className="col-span-2">
              <Label className="text-xs">Payee</Label>
              <Select value={c.payee_id} onValueChange={(v) => setC({ ...c, payee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select payee" /></SelectTrigger>
                <SelectContent>{payees.map((p) => <SelectItem key={p.payee_id} value={p.payee_id}>{p.legal_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input placeholder="Treaty country (e.g. GB)" value={c.treaty_country} onChange={(e) => setC({ ...c, treaty_country: e.target.value })} />
            <Select value={c.payment_classification} onValueChange={(v) => setC({ ...c, payment_classification: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CLASSIFICATIONS.map((x) => <SelectItem key={x} value={x}>{x.replace(/_/g, ' ').toLowerCase()}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Treaty article (e.g. 12)" value={c.treaty_article} onChange={(e) => setC({ ...c, treaty_article: e.target.value })} />
            <Input placeholder="Approved rate (0.08)" value={c.approved_rate} onChange={(e) => setC({ ...c, approved_rate: e.target.value })} />
            <div><Label className="text-xs">Valid from</Label><Input type="date" value={c.valid_from} onChange={(e) => setC({ ...c, valid_from: e.target.value })} /></div>
            <div><Label className="text-xs">Valid to</Label><Input type="date" value={c.valid_to} onChange={(e) => setC({ ...c, valid_to: e.target.value })} /></div>
            <Input className="col-span-2" placeholder="Certificate of residence reference" value={c.certificate_ref} onChange={(e) => setC({ ...c, certificate_ref: e.target.value })} />
            <Input className="col-span-2" placeholder="Reason" value={c.reason} onChange={(e) => setC({ ...c, reason: e.target.value })} />
            <div className="col-span-2 flex justify-end"><Button size="sm" onClick={create} disabled={busy === 'new'}>Submit for approval</Button></div>
          </div>
        )}
        {claims.length === 0 ? (
          <p className="text-sm text-muted-foreground">No treaty claims. Create one (with a certificate of residence) to relieve a non-resident payment.</p>
        ) : (
          <div className="space-y-1.5">
            {claims.map((cl) => (
              <div key={cl.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2.5 text-sm">
                <div>
                  <span className="font-medium text-foreground">{payeeName(cl.payee_id)}</span>
                  <span className="text-muted-foreground"> · {cl.treaty_country} art.{cl.treaty_article} · {(Number(cl.approved_rate) * 100).toFixed(2)}% · valid {cl.valid_from}→{cl.valid_to}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge variant={STATUS[cl.status] ?? 'secondary'}>{cl.status}</Badge>
                  {cl.status === 'pending' && canApprove && (
                    <>
                      <Button size="sm" variant="outline" disabled={busy === cl.id} onClick={() => action(cl.id, 'approve')}>Approve</Button>
                      <Button size="sm" variant="ghost" disabled={busy === cl.id} onClick={() => action(cl.id, 'reject')}>Reject</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
